// Network · Events approval cards, shown on Home and on the Events page (lib/nav.ts TASK_HOMES):
// 2.24 choose an event (choose_event) and 2.25 approve a pitch to its organiser (approve_event_pitch). Both go through
// act; the database checks that a person decided, and sends a pitch only to the organiser's published contact.
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { Notice } from "../../components/ui";
import { Decide } from "../approvals/TaskCards";
import { calendarNote, choicesFor, deadlineText, eventDates, PITCH_LABEL, ROLE_LABEL, type CalendarCheck, type EventChoice, type EventRole, type PitchKind } from "../../lib/events";

interface CardProps {
  crm: Crm;
  task: HumanTask;
  onDone: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 2.24 What the owner's calendar says about the event's dates. A clash warns; it never stops a choice. */
export function CalendarLine({ check }: { check: CalendarCheck | null | undefined }) {
  const n = calendarNote(check);
  if (n.tone === "off") {
    return (
      <p className="small muted" data-calendar="off">
        {n.text} <Link to="/settings/connections">Your Accounts</Link>
      </p>
    );
  }
  return (
    <p className={`small${n.tone === "clash" ? " is-late" : " muted"}`} data-calendar={n.tone} role={n.tone === "clash" ? "note" : undefined}>
      {n.text}
    </p>
  );
}

/** The buttons to choose what to do at an event, or skip it. */
export function ChooseButtons({ roles, busy, onChoose, label }: { roles: EventRole[]; busy: boolean; onChoose: (c: EventChoice) => void; label: string }) {
  const choices = choicesFor({ roles });
  return (
    <div className="action-bar" role="group" aria-label={label}>
      {choices.map((r) => (
        <button key={r} type="button" className={`btn btn-small${r === "speak" || (r === "attend" && !choices.includes("speak")) ? " btn-primary" : ""}`} disabled={busy} onClick={() => onChoose(r)}>
          {ROLE_LABEL[r]}
        </button>
      ))}
      <button type="button" className="btn btn-small btn-quiet" disabled={busy} onClick={() => onChoose("skip")}>
        Skip
      </button>
    </div>
  );
}

// 2.24 ------------------------------------------------------------------------------------------
export function ChooseEventTask({ crm, task, onDone }: CardProps) {
  const p = task.payload;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasons = Array.isArray(p.reasons) ? (p.reasons as string[]) : [];
  const roles = (Array.isArray(p.roles) ? p.roles : []) as EventRole[];
  const dl = deadlineText({ deadline: (p.deadline as string) ?? null, deadline_kind: (p.deadline_kind as never) ?? null }, today());

  async function choose(choice: EventChoice) {
    setBusy(true);
    setError(null);
    try {
      await crm.decideEvent(String(p.event_id), choice);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div>
      <p>
        <strong>{String(p.name ?? "An event")}</strong>
        {p.starts_on ? <span className="muted small"> · {eventDates({ starts_on: String(p.starts_on), ends_on: null })}</span> : null}
        {typeof p.score === "number" ? <span className="chip chip-info"> Fit {Math.round(p.score)}</span> : null}
      </p>
      {dl && <p className={`small${dl.urgent ? " is-late" : " muted"}`}>{dl.text}</p>}
      <CalendarLine check={(p.calendar as CalendarCheck | undefined) ?? null} />
      {reasons.length > 0 && (
        <ul className="small" aria-label="Why it was shortlisted">
          {reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      <p className="muted small">Choose what you would do there. Speaking, exhibiting, sponsoring or an award entry means the engine drafts a pitch to the organiser for you to approve.</p>
      <Notice text={error} tone="bad" />
      <ChooseButtons roles={roles} busy={busy} onChoose={(c) => void choose(c)} label={`Choose ${String(p.name ?? "the event")}`} />
    </div>
  );
}

// 2.25 ------------------------------------------------------------------------------------------
export function EventPitchTask({ crm, task, onDone }: CardProps) {
  const p = task.payload;
  const [subject, setSubject] = useState(String(p.subject ?? ""));
  const [body, setBody] = useState(String(p.body ?? ""));
  const kind = PITCH_LABEL[(p.kind as PitchKind) ?? "speaker"] ?? "Pitch";
  const edited = subject !== String(p.subject ?? "") || body !== String(p.body ?? "");
  return (
    <div>
      <p>
        <strong>{p.follow_up ? `Follow-up: ${kind.toLowerCase()}` : kind}</strong> for <strong>{String(p.name ?? "the event")}</strong>
      </p>
      <p className="muted small">
        To {String(p.to_email ?? "the organiser")}, the address the organiser publishes. {p.follow_up ? "This is the one follow-up: there will be no more." : "At most one follow-up follows if there is no answer."}
      </p>
      <label className="field">
        <span className="field-label">Subject</span>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} aria-label="Pitch subject" />
      </label>
      <label className="field">
        <span className="field-label">Message</span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} rows={8} aria-label="Pitch message" />
      </label>
      <Decide
        approveLabel={edited ? "Approve with my edits" : "Approve and send"}
        approveDisabled={subject.trim().length < 3 || body.trim().length < 20}
        onApprove={async () => {
          await crm.decideEventPitch(String(p.event_id), String(p.action_id), true, edited ? { subject, body } : {});
          onDone();
        }}
        onReject={async (note) => {
          await crm.decideEventPitch(String(p.event_id), String(p.action_id), false, { note: note || "Not this one" });
          onDone();
        }}
      />
    </div>
  );
}
